import imageCompression from 'browser-image-compression';

/** Desired output size; we try hard to land at or below this. */
const TARGET_MB = 1;

/**
 * Absolute ceiling the output must NEVER exceed. If we can't get below this, we
 * throw IMG_COMPRESS_FAILED instead of silently returning the raw image — that
 * silent fallthrough is exactly what made huge iPhone photos reach the backend
 * and trip its 10MB limit.
 */
const HARD_MAX_MB = 2;

/** Error code thrown when an image can't be compressed under HARD_MAX_MB. */
export const IMG_COMPRESS_FAILED = 'IMG_COMPRESS_FAILED';

/** Dimension/quality ladders for the canvas fallback, most → least quality. */
const DIMENSION_LADDER = [2048, 1600, 1280, 1024, 800];
const QUALITY_LADDER = [0.82, 0.7, 0.6, 0.5];

/** Compression options - matches backend settings for consistency */
const COMPRESSION_OPTIONS = {
  maxSizeMB: TARGET_MB,      // Max 1MB output
  maxWidthOrHeight: 2048,    // Match backend MAX_DIMENSION
  useWebWorker: false,       // Disabled for native (Capacitor/Cordova) WebView compatibility
  fileType: 'image/jpeg' as const,
  initialQuality: 0.82,      // Match backend JPEG_QUALITY
};

/** Bytes of a base64 data URL's payload, in MB. */
function base64SizeMB(base64: string): number {
  const data = base64.includes(',') ? base64.split(',')[1] : base64;
  return ((data.length * 3) / 4) / (1024 * 1024);
}

/**
 * Compress an image file before upload.
 * Reduces file size significantly while maintaining good quality.
 *
 * @param file - The original image file
 * @returns Compressed image file (JPEG, max 1MB, max 2048px)
 */
export async function compressImageFile(file: File): Promise<File> {
  const hardMaxBytes = HARD_MAX_MB * 1024 * 1024;

  // First try the library. Accept its result only if valid AND under the ceiling.
  try {
    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
    if (compressed && compressed.size > 0 && compressed.size <= hardMaxBytes) {
      const reduction = ((1 - compressed.size / file.size) * 100).toFixed(1);
      console.log(
        `[Compression] Library: ${(file.size / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB (${reduction}%)`
      );
      return compressed;
    }
    console.warn('[Compression] Library result empty or still too large, using canvas ladder');
  } catch (error) {
    console.error('[Compression] Library failed, using canvas ladder:', error);
  }

  // Canvas ladder is the real safety net. It throws IMG_COMPRESS_FAILED if it
  // cannot get under the ceiling — we never return an oversized original.
  return iterativeCanvasCompress(file);
}

/**
 * Canvas-based compression that iteratively downscales/lowers quality until the
 * output is under HARD_MAX_MB. Used when browser-image-compression fails or its
 * result is still too large (common on iOS WKWebView with big iPhone photos).
 *
 * The source image is decoded ONCE; each ladder rung only re-draws/re-encodes,
 * to keep memory pressure low on the WebView. REJECTS if no rung gets under the
 * ceiling — it never returns the oversized original.
 */
async function iterativeCanvasCompress(file: File): Promise<File> {
  // Load directly from a data URL. `URL.createObjectURL(blob)` + <img> can fail
  // silently in iOS WKWebView; a base64 data URL is more reliable there.
  const dataUrl = await fileToBase64(file);
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  const hardMaxBytes = HARD_MAX_MB * 1024 * 1024;
  const targetBytes = TARGET_MB * 1024 * 1024;
  // iOS gives natural dimensions; fall back to width/height if needed.
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  let smallest: File | null = null;

  for (const maxDim of DIMENSION_LADDER) {
    // Scale longest edge down to maxDim (never upscale).
    let width = srcW;
    let height = srcH;
    if (width > maxDim || height > maxDim) {
      if (width >= height) {
        height = Math.round((height / width) * maxDim);
        width = maxDim;
      } else {
        width = Math.round((width / height) * maxDim);
        height = maxDim;
      }
    }
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of QUALITY_LADDER) {
      const blob = await canvasToBlob(canvas, quality);
      if (!blob || blob.size === 0) {
        continue;
      }
      const candidate = new File([blob], file.name, {
        type: 'image/jpeg',
        lastModified: file.lastModified || 0,
      });
      if (!smallest || candidate.size < smallest.size) {
        smallest = candidate;
      }
      // Good enough — stop early.
      if (candidate.size <= targetBytes) {
        console.log(
          `[Canvas Compression] ${(file.size / 1024).toFixed(0)}KB → ${(candidate.size / 1024).toFixed(0)}KB @ ${maxDim}px q${quality}`
        );
        return candidate;
      }
    }
  }

  if (smallest && smallest.size <= hardMaxBytes) {
    console.log(
      `[Canvas Compression] best effort ${(smallest.size / 1024).toFixed(0)}KB (<= hard max)`
    );
    return smallest;
  }

  throw new Error(IMG_COMPRESS_FAILED);
}

/** Promisified image decode with a timeout so a hung decode rejects cleanly. */
function loadImage(src: string, timeoutMs = 15000): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error('Image load timeout'));
      }
    }, timeoutMs);
    img.onload = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error('Image load error'));
    };
    img.src = src;
  });
}

/** Promisified canvas.toBlob as JPEG. */
function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Compress an image file and convert to base64 data URL.
 *
 * @param file - The original image file
 * @returns Base64 data URL of compressed image
 */
export async function compressImageToBase64(file: File): Promise<string> {
  const compressed = await compressImageFile(file);
  return fileToBase64(compressed);
}

/**
 * Compress a base64 image string.
 * Useful for images from camera plugin or other sources already in base64.
 *
 * @param base64 - Base64 data URL string
 * @param skipIfAlreadyCompressed - Skip compression if image is already small enough
 * @returns Compressed base64 data URL
 */
export async function compressBase64Image(
  base64: string,
  skipIfAlreadyCompressed: boolean = false
): Promise<string> {
  const match = base64.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    throw new Error('Invalid base64 string');
  }

  const currentSizeInMB = base64SizeMB(base64);
  console.log(`[Base64 Compression] Current size: ${currentSizeInMB.toFixed(2)}MB`);

  // Skip only when the image is already at/below TARGET. Since TARGET < HARD_MAX,
  // this can never let an oversized image through to the backend.
  if (skipIfAlreadyCompressed && currentSizeInMB <= TARGET_MB) {
    console.log('[Base64 Compression] Skipping - already within target size');
    return base64;
  }

  // If it's already small enough we still don't need to recompress.
  if (currentSizeInMB <= TARGET_MB) {
    return base64;
  }

  // The image is already acceptable (<= HARD_MAX) when it only exceeds TARGET.
  // We still try to shrink it toward TARGET, but if compression fails on this
  // device (common on iOS WKWebView), the original is fine to use — only a
  // truly oversized image (> HARD_MAX) must be rejected.
  const alreadyAcceptable = currentSizeInMB <= HARD_MAX_MB;

  try {
    // Pure base64/canvas path — never touches FileReader or Blob, both of which
    // can hang in iOS WKWebView (this was the cause of the cropper never opening).
    const result = await canvasCompressBase64(base64);
    console.log(`[Base64 Compression] Final size: ${base64SizeMB(result).toFixed(2)}MB`);
    return result;
  } catch (error) {
    if (alreadyAcceptable) {
      console.warn('[Base64 Compression] Compression failed but image is within the hard limit — using original');
      return base64;
    }
    // Oversized AND we couldn't shrink it — surface so the caller warns the user
    // instead of shipping a raw multi-MB image (the original bug).
    throw error;
  }
}

/**
 * Compresses a base64 data URL using only <img> + canvas + canvas.toDataURL.
 * Intentionally avoids FileReader/Blob/createObjectURL, which can hang silently
 * in iOS WKWebView. Iterates a dimension/quality ladder until <= TARGET; returns
 * the smallest result <= HARD_MAX, or throws IMG_COMPRESS_FAILED.
 */
async function canvasCompressBase64(base64: string): Promise<string> {
  const img = await loadImage(base64);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) {
    throw new Error(IMG_COMPRESS_FAILED);
  }

  let smallest: string | null = null;
  let smallestMB = Infinity;

  for (const maxDim of DIMENSION_LADDER) {
    let width = srcW;
    let height = srcH;
    if (width > maxDim || height > maxDim) {
      if (width >= height) {
        height = Math.round((height / width) * maxDim);
        width = maxDim;
      } else {
        width = Math.round((width / height) * maxDim);
        height = maxDim;
      }
    }
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    for (const quality of QUALITY_LADDER) {
      const out = canvas.toDataURL('image/jpeg', quality);
      const mb = base64SizeMB(out);
      if (mb > 0 && mb < smallestMB) {
        smallest = out;
        smallestMB = mb;
      }
      if (mb > 0 && mb <= TARGET_MB) {
        console.log(`[Canvas Compression] -> ${mb.toFixed(2)}MB @ ${maxDim}px q${quality}`);
        return out;
      }
    }
  }

  if (smallest && smallestMB <= HARD_MAX_MB) {
    console.log(`[Canvas Compression] best effort ${smallestMB.toFixed(2)}MB (<= hard max)`);
    return smallest;
  }

  throw new Error(IMG_COMPRESS_FAILED);
}

/**
 * Convert a File to base64 data URL
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Convert a base64 data URL to File
 */
function base64ToFile(base64: string): File {
  // Extract mime type and data
  const match = base64.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    throw new Error('Invalid base64 string');
  }

  const mimeType = match[1];
  const data = match[2];

  // Decode base64
  const byteString = atob(data);
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  // Create File
  const blob = new Blob([byteArray], { type: mimeType });
  return new File([blob], 'image.jpg', { type: mimeType });
}
